<template>
  <div class="card">
    <div
      class="card-header d-flex align-items-center justify-content-between flex-wrap"
      style="gap: 8px"
    >
      <span class="card-title mb-0">{{ $t('team.list.title') }}</span>
      <div
        class="d-flex align-items-center"
        style="gap: 8px"
      >
        <b-form-checkbox
          v-if="hasChildren"
          v-model="incluirSubnos"
          switch
          class="mr-50"
          @change="carregar"
        >
          {{ $t('team.list.includeSubNodes') }}
        </b-form-checkbox>
        <select
          v-if="mostrarNo && nos.length > 1"
          v-model="filtroNo"
          class="form-control form-control-sm"
          style="width: 180px"
        >
          <option :value="null">
            {{ $t('team.list.allNodes') }}
          </option>
          <option
            v-for="no in nos"
            :key="no.id"
            :value="no.id"
          >
            {{ no.name }}
          </option>
        </select>
        <input
          v-model="busca"
          type="text"
          class="form-control form-control-sm"
          style="width: 200px"
          :placeholder="$t('team.list.searchPlaceholder')"
        >
        <b-button
          v-if="$hasAbility('equipe.gerenciar')"
          size="sm"
          variant="primary"
          @click="abrirForm(null)"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('team.list.newButton') }}
        </b-button>
      </div>
    </div>

    <div
      v-if="carregando"
      class="text-center py-4"
    >
      <b-spinner
        small
        variant="primary"
      />
    </div>

    <div
      v-else-if="filtradas.length === 0"
      class="text-center text-muted py-4"
    >
      {{ busca || filtroNo ? $t('team.list.noResults') : $t('team.list.empty') }}
    </div>

    <table
      v-else
      class="table table-sm table-hover mb-0"
    >
      <thead>
        <tr>
          <th>{{ $t('team.crud.nameColumn') }}</th>
          <th v-if="mostrarNo">
            {{ $t('team.list.nodeColumn') }}
          </th>
          <th>{{ $t('team.list.typeColumn') }}</th>
          <th>{{ $t('team.list.disciplineColumn') }}</th>
          <th>{{ $t('team.list.identifierCodeColumn') }}</th>
          <th>{{ $t('common.status') }}</th>
          <th class="text-right">
            {{ $t('common.actions') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="team in filtradas"
          :key="team.id"
        >
          <td>{{ team.name }}</td>
          <td v-if="mostrarNo">
            <span :class="{ 'text-muted': Number(team.node_id) === Number(nodeId) }">
              {{ team.node ? team.node.name : '—' }}
            </span>
          </td>
          <td>
            <span v-if="team.type">{{ team.type.name }}</span>
            <span
              v-else
              class="text-warning"
              :title="$t('team.list.untypedHint')"
            >{{ $t('team.list.untyped') }}</span>
          </td>
          <td>
            <span v-if="team.discipline">{{ team.discipline.name }}</span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </td>
          <td>
            <span
              v-if="team.frotas_identifier_code"
              class="badge badge-secondary text-monospace"
            >{{ team.frotas_identifier_code }}</span>
            <span
              v-else
              class="text-muted"
            >—</span>
          </td>
          <td>
            <b-badge :variant="Number(team.status) === 1 ? 'light-success' : 'light-secondary'">
              {{ Number(team.status) === 1 ? $t('common.active') : $t('common.inactive') }}
            </b-badge>
          </td>
          <td
            class="text-right"
            style="white-space: nowrap"
          >
            <template v-if="podeGerenciar(team)">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-50"
                :title="$t('common.edit')"
                @click="abrirForm(team)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="13"
                />
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                :title="$t('common.delete')"
                @click="excluir(team)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="13"
                />
              </b-button>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <b-modal
      v-model="mostrarForm"
      :title="equipeEditando ? $t('team.form.editTitle') : $t('team.form.newTitle')"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <team-form
        v-if="mostrarForm"
        :team-id="equipeEditando ? equipeEditando.id : null"
        :node-id="nodeId"
        @saved="aoSalvar"
        @cancel="mostrarForm = false"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BSpinner, BBadge, BFormCheckbox, BButton, BModal,
} from 'bootstrap-vue'
import swal from 'sweetalert'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import TeamForm from './TeamForm.vue'

/**
 * Equipes de um nó, usada na aba "Equipes" da administração do nó.
 * Lista corrida (sem paginação): uma central tem dezenas de equipes, não
 * milhares, e a busca local responde sem ida ao servidor.
 *
 * Num nó com filhos, inclui por padrão as equipes dos sub-nós (o pai
 * administra o que está abaixo dele) e mostra a coluna do nó dono.
 */
export default {
  name: 'TeamIndex',
  components: {
    BSpinner, BBadge, BFormCheckbox, BButton, BModal, TeamForm,
  },
  mixins: [ApiErrorMixin],

  props: {
    nodeId: {
      type: [Number, String],
      required: true,
    },
    hasChildren: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      equipes: [],
      busca: '',
      filtroNo: null,
      mostrarForm: false,
      equipeEditando: null,
      incluirSubnos: this.hasChildren,
      carregando: false,
    }
  },

  computed: {
    mostrarNo() {
      return this.hasChildren && this.incluirSubnos
    },

    // Nós presentes na lista, o administrado primeiro.
    nos() {
      const porId = {}
      this.equipes.forEach(t => {
        if (t.node) porId[t.node.id] = t.node
      })

      return Object.values(porId).sort((a, b) => this.ordemNo(a.id) - this.ordemNo(b.id)
        || a.name.localeCompare(b.name))
    },

    filtradas() {
      const termo = this.busca.trim().toLowerCase()

      return this.equipes
        .filter(t => !this.filtroNo || Number(t.node_id) === Number(this.filtroNo))
        .filter(t => !termo
          || (t.name || '').toLowerCase().includes(termo)
          || (t.frotas_identifier_code || '').toLowerCase().includes(termo))
    },
  },

  watch: {
    // A árvore de nós da tela pai pode chegar depois desta aba montar
    // (abertura direta por ?tab=teams).
    hasChildren(tem) {
      this.incluirSubnos = tem
      this.carregar()
    },
  },

  mounted() {
    this.carregar()
  },

  methods: {
    ordemNo(id) {
      return Number(id) === Number(this.nodeId) ? 0 : 1
    },

    // Equipe de sub-nó exige node.admin — mesma regra do TeamService.
    podeGerenciar(team) {
      return this.$hasAbility('equipe.gerenciar')
        && (Number(team.node_id) === Number(this.nodeId) || this.$hasAbility('node.admin'))
    },

    async carregar() {
      this.carregando = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}teams`, {
          params: {
            node_id: this.nodeId,
            with_descendants: this.mostrarNo ? 1 : 0,
            // sharedNodes evita o N+1 do atributo anexado shared_node_ids.
            with: ['type', 'discipline', 'sharedNodes', 'node'],
            paginated: false,
            orderByAsc: 'name',
          },
        })
        const equipes = res.data.data || []

        // Equipes do nó administrado primeiro, depois agrupadas por sub-nó.
        this.equipes = this.mostrarNo
          ? equipes.slice().sort((a, b) => this.ordemNo(a.node_id) - this.ordemNo(b.node_id)
            || ((a.node && a.node.name) || '').localeCompare((b.node && b.node.name) || '')
            || (a.name || '').localeCompare(b.name || ''))
          : equipes

        if (this.filtroNo && !this.nos.some(n => Number(n.id) === Number(this.filtroNo))) {
          this.filtroNo = null
        }
      } catch (e) {
        this.equipes = []
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('team.list.loadError')), 'error')
      } finally {
        this.carregando = false
      }
    },

    abrirForm(team) {
      this.equipeEditando = team
      this.mostrarForm = true
    },

    aoSalvar() {
      this.mostrarForm = false
      this.carregar()
    },

    async excluir(team) {
      const ok = await swal({
        title: this.$t('team.list.deleteConfirmTitle', { name: team.name }),
        text: this.$t('team.list.deleteConfirmText'),
        icon: 'warning',
        buttons: [this.$t('common.cancel'), this.$t('common.delete')],
        dangerMode: true,
      })
      if (!ok) return

      try {
        await this.$http.delete(`${this.$store.state.api}teams/${team.id}`, {
          params: { node_id: this.nodeId },
        })
        this.$message(this.$t('team.messages.saveSuccessTitle'), this.$t('team.list.deletedMessage'), 'success')
        this.carregar()
      } catch (e) {
        this.$message(this.$t('common.error'), this.apiErrorText(e, this.$t('team.list.deleteError')), 'error')
      }
    },
  },
}
</script>
